import React from 'react';
import { badgeGeometry, STATUSES } from '../../utils/presenceStatus';
import './PresenceBadge.css';

/**
 * The status badge that overlays an avatar's bottom-right corner.
 *
 * Purely presentational — no stores, no hooks, no i18n. It renders `null` for
 * a missing status (an absent user, a free/local-only account, a socket that
 * is down) rather than a grey "offline" dot, and `null` for avatars under
 * 18px, where a legible badge does not fit.
 *
 * Mount it as a CHILD of `<UserAvatar>`: `AvatarShell` is `position:relative`
 * and renders children last, which is the overlay slot its docblock promises
 * and the unread badge already uses.
 *
 * The states are told apart by SHAPE, not colour alone:
 *   online  — solid disc
 *   away    — crescent (a disc knocked out of the upper right)
 *   busy    — disc with a horizontal bar through the middle
 *   invisible — a donut: a filled ring with the centre punched out in the
 *               surface colour, which is the shape every chat app uses for
 *               "offline". That is deliberate — invisible MEANS "appear
 *               offline", so it must not look like a fourth active state
 *   offline   — THE SAME DONUT. `--offline` is a CSS alias of `--invisible`,
 *               not a lookalike: the privacy promise is that a hiding user is
 *               INDISTINGUISHABLE from an absent one, and two independently
 *               authored greys drift. Both branches below therefore key on
 *               `isDonut`, so a future edit cannot change one and not the
 *               other. `presenceBadgePixelIdentity.test.js` compares the two
 *               rendered trees attribute for attribute.
 * The knockout is painted in the ring colour, so it reads on both themes.
 *
 * `status` is only ever rendered when it is in `STATUSES`; an unknown status —
 * including `null`, which is what "we have not been told anything about this
 * user" resolves to — renders nothing at all. That is not the same as
 * `offline`, and merging the two is the whole failure mode this feature had to
 * avoid.
 */
export default function PresenceBadge({ status, avatarSize, label, className = '' }) {
  if (!STATUSES.includes(status)) return null;

  const geometry = badgeGeometry(avatarSize);
  if (!geometry) return null;

  const { d, ring } = geometry;
  // Offline and invisible are the same donut, by construction.
  const isDonut = status === 'invisible' || status === 'offline';

  return (
    <span
      className={`presence-badge${className ? ` ${className}` : ''}`}
      role="img"
      aria-label={label || status}
      data-status={status}
      style={{ width: d, height: d, borderWidth: ring }}
    >
      <svg
        className="presence-badge__svg"
        viewBox="0 0 16 16"
        width="100%"
        height="100%"
        aria-hidden="true"
        focusable="false"
      >
        <circle className={`presence-badge__disc--${status}`} cx="8" cy="8" r="8" />
        {status === 'away' && (
          <circle className="presence-badge__knockout" cx="12.6" cy="3.4" r="6.2" />
        )}
        {isDonut && (
          /* The hole. Filled with the ring colour (the surface behind the
             badge), so the donut reads as hollow in either theme without
             needing a mask. */
          <circle className="presence-badge__hole" cx="8" cy="8" r="3.6" />
        )}
        {status === 'busy' && (
          <rect
            className="presence-badge__knockout"
            x="2.6"
            y="6.6"
            width="10.8"
            height="2.8"
            rx="1.4"
          />
        )}
      </svg>
    </span>
  );
}
